import { __assign, __awaiter, __extends, __generator, __read, __rest, __spread } from "tslib";
import { VisitStatuses } from '@goodrx/heydoctor-core';
import { find, pick } from 'lodash';
import Model from './model';
import { getUtcTimestamp } from '../utils';
var REQUIRED_FIELDS = ['firstName', 'lastName', 'email', 'dob', 'phone', 'gender', 'zip'];
var UPDATABLE_FIELDS = [
    'firstName',
    'lastName',
    'dob',
    'phone',
    'gender',
    'email',
    'zip',
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'zip',
];
var User = (function (_super) {
    __extends(User, _super);
    function User() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.isProfileComplete = function () {
            return REQUIRED_FIELDS.every(function (field) { return !!_this.data[field]; });
        };
        return _this;
    }
    User.prototype.update = function (updates) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.patch('/users/me', pick(updates, UPDATABLE_FIELDS))];
                    case 1:
                        _a.sent();
                        return [2, this.createSelf(updates)];
                }
            });
        });
    };
    User.prototype.updateNotificationTopic = function (_a) {
        var topic = _a.topic, channel = _a.channel, status = _a.status;
        return __awaiter(this, void 0, void 0, function () {
            var notifications, preferenceExists, updatedNotifications;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4, this.client.http.post("/users/me/notifications/" + topic, { channel: channel, status: status })];
                    case 1:
                        _b.sent();
                        notifications = this.data.notifications;
                        preferenceExists = find(notifications, { topic: topic, channel: channel });
                        updatedNotifications = notifications.length && preferenceExists
                            ? notifications.map(function (n) { return (n.channel === channel && n.topic === topic ? __assign(__assign({}, n), { status: status }) : n); })
                            : __spread(notifications, [{ topic: topic, channel: channel, status: status }]);
                        return [2, this.createSelf({ notifications: updatedNotifications })];
                }
            });
        });
    };
    User.prototype.addDevice = function (data) {
        return __awaiter(this, void 0, void 0, function () {
            var device;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post('/users/me/devices', data)];
                    case 1:
                        device = (_a.sent()).data.data;
                        return [2, this.createSelf({
                                devices: __spread(this.data.devices, [device]),
                            })];
                }
            });
        });
    };
    User.prototype.updateDevice = function (_a) {
        var deviceId = _a.deviceId, updates = __rest(_a, ["deviceId"]);
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4, this.client.http.patch("/users/me/devices/" + deviceId, updates)];
                    case 1:
                        _b.sent();
                        return [2, this.createSelf({
                                devices: this.data.devices.map(function (device) {
                                    if (device.deviceId === deviceId) {
                                        return __assign(__assign({}, device), updates);
                                    }
                                    return device;
                                }),
                            })];
                }
            });
        });
    };
    User.prototype.getDevice = function (deviceId) {
        return this.devices.find(function (d) { return d.deviceId === deviceId; });
    };
    User.prototype.getPaymentMethods = function () {
        return __awaiter(this, void 0, void 0, function () {
            var paymentMethods;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.get('/users/me/payment')];
                    case 1:
                        paymentMethods = (_a.sent()).data.data;
                        return [2, paymentMethods];
                }
            });
        });
    };
    User.prototype.savePaymentMethod = function (token) {
        return __awaiter(this, void 0, void 0, function () {
            var paymentMethod;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post('/users/me/payment', { token: token })];
                    case 1:
                        paymentMethod = (_a.sent()).data.data;
                        return [2, paymentMethod];
                }
            });
        });
    };
    User.prototype.deletePaymentMethod = function (token) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2, this.client.http.delete("/users/me/payment/" + token)];
            });
        });
    };
    User.prototype.getLegalAgreements = function () {
        return this.client.http.get('/users/me/legals').then(function (_a) {
            var userLegals = _a.data.data;
            return userLegals;
        });
    };
    User.prototype.updateLegalAgreements = function (codes) {
        return __awaiter(this, void 0, void 0, function () {
            var legalDocuments;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post('/users/me/legals', { codes: codes })];
                    case 1:
                        legalDocuments = (_a.sent()).data.data;
                        return [2, legalDocuments];
                }
            });
        });
    };
    User.prototype.revokeLegalAgreement = function (code) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.delete("/users/me/legals/" + code)];
                    case 1:
                        _a.sent();
                        return [2, this.data.legalDocuments.filter(function (doc) { return doc.code !== code; })];
                }
            });
        });
    };
    User.prototype.sendPhoneVerification = function () {
        return this.client.http.post('/users/me/request-phone-verification', null, { hideSuccessAlert: true });
    };
    User.prototype.verifyPhone = function (code) {
        return this.client.http.post('/users/me/verify-phone', { code: code });
    };
    User.prototype.getExistingVisits = function (serviceCode) {
        return this.client.http
            .get("/visits?serviceCode=" + serviceCode + "&status=" + VisitStatuses.PRE_VISIT_INTAKE + "," + VisitStatuses.READY_FOR_PROVIDER + "&limit=1")
            .then(function (_a) {
            var existingVisits = _a.data.data;
            return existingVisits;
        });
    };
    User.prototype.fetchPaymentMethods = function () {
        return this.client.http.get('/users/me/payment').then(function (_a) {
            var data = _a.data.data;
            return data;
        });
    };
    User.prototype.fetchSubscription = function () {
        return this.client.http.get('/users/me/membership').then(function (_a) {
            var data = _a.data.data;
            return data;
        });
    };
    User.prototype.createSubscription = function (payload) {
        return this.client.http.post('/users/me/membership', payload).then(function (_a) {
            var data = _a.data.data;
            return data;
        });
    };
    User.prototype.cancelSubscription = function () {
        return this.client.http.delete('/users/me/membership');
    };
    User.prototype.verifyEmail = function (emailToken) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, this.client.http.post('/auth/verify-email', { emailToken: emailToken })];
                    case 1:
                        _a.sent();
                        return [2, this.createSelf({ emailVerifiedAt: getUtcTimestamp() })];
                }
            });
        });
    };
    User.prototype.resendEmailVerification = function () {
        return this.client.http.post('/users/me/send-email-verification');
    };
    Object.defineProperty(User.prototype, "address", {
        get: function () {
            return {
                phone: this.data.phone,
                addressLine1: this.data.addressLine1,
                addressLine2: this.data.addressLine2,
                city: this.data.city,
                state: this.data.state,
                zip: this.data.zip,
                zip4: this.data.zip4,
            };
        },
        enumerable: false,
        configurable: true
    });
    return User;
}(Model));
export default User;
//# sourceMappingURL=user.js.map